import { CssUtils } from '@common/flare-data/util/css-utils';
import { ProseMirrorNode } from 'prosemirror-model';
import { Command, EditorState, EditorStateConfig, Plugin, PluginKey, Transaction } from 'prosemirror-state';
import { Decoration, DecorationSet, EditorView } from 'prosemirror-view';

export const FlareComputedStylesheetSetMetaKey: string = 'flare-computed-stylesheet.set';
export const FlareComputedStylesheetPluginKey: PluginKey = new PluginKey('flare-computed-stylesheet');

export interface FlareComputedStylesheetPluginState {
  /** Whether or not topic stylesheets are enabled. When enabled topic styles are applied to the editor content. */
  readonly enabled: boolean;
  /** Whether or not to show the styles panel in the editor. */
  readonly showPanel: boolean;
  /** Decorations used to add style attributes in the editor when styles are enabled. */
  readonly decorations: DecorationSet;
}

/** Plugin options for computed stylesheet behavior. */
export interface FlareComputedStylesheetPluginOptions {
  /** Initial enabled state. Defaults to false. */
  enabled?: boolean;
  /** Initial panel visibility state. Defaults to false. */
  showPanel?: boolean;
  /** Gets editor class attribute from the current plugin state. */
  getEditorClassName?: (state: FlareComputedStylesheetPluginState) => string;
  /** Called when transaction meta explicitly sets the `enabled` field. */
  onEnabledChanged?: (enabled: boolean) => void;
  /** Called after style decorations have been built. */
  onStyleDecorationsBuilt?: (decorations: DecorationSet) => void;
  /** Optional plugin view factory. */
  createView?: (view: EditorView) => { destroy?: () => void };
  /** Optional callback used to rewrite links found in CSS style values. */
  updateStyleLink?: (link: string) => string;
}

const mcStylePropertyNameRegex = /^mc-/gi;

function buildStyleString(styles: Dictionary<string>, updateStyleLink?: (link: string) => string): string {
  let styleString = '';

  for (let [name, value] of Object.entries(styles)) {
    if (typeof value === 'string') {
      // Convert 'mc-' styles with '--mc-' CSS variables to make the styles work with the node views
      name = name.replace(mcStylePropertyNameRegex, '--mc-');

      // Convert url() values to point to the correct location for the LWE
      value = CssUtils.updateLinksInValue(value, link => updateStyleLink?.(link) ?? link);

      // Add the style declaration to the style string
      styleString += name + ': ' + value + '; ';
    }
  }

  return styleString.trimEnd();
}

function buildStyleDecorations(doc: ProseMirrorNode, enabled: boolean, updateStyleLink?: (link: string) => string): DecorationSet {
  if (!enabled) {
    return DecorationSet.empty;
  }

  const decorations: Decoration[] = [];

  doc.descendants((node, pos) => {
    if (node.isText || node.type.spec.noStyles) {
      return;
    }

    const style = node.attrs?.style;

    if (style && Object.keys(style).length > 0) {
      decorations.push(Decoration.node(pos, pos + node.nodeSize, { style: buildStyleString(style, updateStyleLink) }));
    }
  });

  return DecorationSet.create(doc, decorations);
}

/**
 * A ProseMirror plugin that tracks stylesheet toggle state and applies style attributes through node decorations.
 */
export function flareComputedStylesheetPlugin(options: FlareComputedStylesheetPluginOptions = {}): Plugin {
  return new Plugin({
    key: FlareComputedStylesheetPluginKey,
    state: {
      init(_config: EditorStateConfig, editorState: EditorState): FlareComputedStylesheetPluginState {
        const enabled = !!options.enabled;
        const showPanel = !!options.showPanel;
        const decorations = buildStyleDecorations(editorState.doc, enabled, options.updateStyleLink);

        options.onStyleDecorationsBuilt?.(decorations);

        return {
          enabled,
          showPanel,
          decorations
        };
      },
      apply(tr: Transaction, stateValue: FlareComputedStylesheetPluginState): FlareComputedStylesheetPluginState {
        const config = tr.getMeta(FlareComputedStylesheetSetMetaKey) as FlareComputedStylesheetPluginState;

        // If there is no explicit config set in the transaction meta and the document did not change, then we can return the existing state value.
        if (!tr.docChanged && !config) {
          return stateValue;
        }

        const enabled = config?.enabled ?? stateValue.enabled;
        const showPanel = config?.showPanel ?? stateValue.showPanel;
        const enabledChanged = enabled !== stateValue.enabled;

        // If the enabled state changed then we need to notify via the onEnabledChanged callback so that any external UI can react to the change.
        if (enabledChanged) {
          options.onEnabledChanged?.(enabled);
        }

        // If the enabled state changed or the document changed then we need to recalculate the decorations
        if (enabledChanged || tr.docChanged) {
          const decorations = buildStyleDecorations(tr.doc, enabled, options.updateStyleLink);

          options.onStyleDecorationsBuilt?.(decorations);

          return {
            enabled,
            showPanel,
            decorations
          };
        }

        // The document did not change, so we can reuse the existing decorations.
        return {
          enabled,
          showPanel,
          decorations: stateValue.decorations
        };
      }
    },
    props: {
      decorations(editorState: EditorState) {
        return this.getState(editorState).decorations;
      },
      attributes(editorState: EditorState) {
        const pluginState = FlareComputedStylesheetPluginKey.getState(editorState) as FlareComputedStylesheetPluginState;
        const className = options.getEditorClassName?.(pluginState);
        return className ? { class: className } : {};
      }
    },
    view(view: EditorView) {
      return options.createView?.(view) ?? {};
    }
  });
}

/** Editor command which sets whether to show computed styles in the editor. */
export declare type FlareComputedStylesheetSetShowStylesCommand = (state: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, showStyles?: boolean) => boolean;

export function setShowStyles(): FlareComputedStylesheetSetShowStylesCommand {
  return function (editorState: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, showStyles?: boolean): boolean {
    if (dispatch) {
      const tr = editorState.tr;
      tr.setMeta(FlareComputedStylesheetSetMetaKey, {
        enabled: showStyles
      } as FlareComputedStylesheetPluginState);
      dispatch(tr);
    }

    return true;
  }
}

export function toggleShowStyles(): Command {
  return function (editorState: EditorState, dispatch?: ProsemirrorDispatcher): boolean {
    const flareComputedStylesheetState: FlareComputedStylesheetPluginState = FlareComputedStylesheetPluginKey.getState(editorState);

    if (dispatch) {
      const tr = editorState.tr;
      tr.setMeta(FlareComputedStylesheetSetMetaKey, {
        enabled: !flareComputedStylesheetState?.enabled
      } as FlareComputedStylesheetPluginState);
      dispatch(tr);
    }

    return true;
  }
}

export function setShowStylesPanel(): FlareComputedStylesheetSetShowStylesCommand {
  return function (editorState: EditorState, dispatch?: ProsemirrorDispatcher, view?: EditorView, showPanel?: boolean): boolean {
    if (dispatch) {
      const tr = editorState.tr;
      tr.setMeta(FlareComputedStylesheetSetMetaKey, {
        showPanel: showPanel
      } as FlareComputedStylesheetPluginState);
      dispatch(tr);
    }

    return true;
  }
}

export function toggleShowStylesPanel(): Command {
  return function (editorState: EditorState, dispatch?: ProsemirrorDispatcher): boolean {
    const flareComputedStylesheetState: FlareComputedStylesheetPluginState = FlareComputedStylesheetPluginKey.getState(editorState);

    if (dispatch) {
      const tr = editorState.tr;
      tr.setMeta(FlareComputedStylesheetSetMetaKey, {
        showPanel: !flareComputedStylesheetState?.showPanel
      } as FlareComputedStylesheetPluginState);
      dispatch(tr);
    }

    return true;
  }
}
